import { useContext, useState } from "react";
import { ButtonDS, GlobalIcon } from "get-life-storybook-ts";
import { I18nContext } from "../../../../../context/i18n.context";

interface SmartFeedbackProps {
  userName: string;
  onClose: () => void;
  trustpilotUrl?: string;
}

type Step = "rating" | "happy" | "unhappy" | "thanks";

const TRUSTPILOT_URL = "https://www.trustpilot.com/evaluate/life5.es";

const SmartFeedback = ({
  userName,
  onClose,
  trustpilotUrl = TRUSTPILOT_URL,
}: SmartFeedbackProps) => {
  const {
    state: { translate },
  } = useContext(I18nContext);

  const [step, setStep] = useState<Step>("rating");
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [feedbackText, setFeedbackText] = useState("");

  const handleRate = (stars: number) => {
    setRating(stars);
    setTimeout(() => {
      setStep(stars >= 4 ? "happy" : "unhappy");
    }, 300);
  };

  const handleSubmitFeedback = () => {
    // TODO: POST /api/feedback/internal
    setStep("thanks");
    setTimeout(onClose, 2000);
  };

  const cardStyle: React.CSSProperties = {
    position: "fixed",
    bottom: 24,
    right: 24,
    zIndex: 900,
    backgroundColor: "#fff",
    borderRadius: 16,
    boxShadow: "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08)",
    padding: 28,
    width: 380,
    maxWidth: "calc(100vw - 48px)",
  };

  const starSize = 36;

  // ── STAR RATING ───────────────────────────────────────────────────────
  const RatingStep = () => (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
        <GlobalIcon iconName="CircleCheckFilledIcon" size="XS" color="#059669" />
        <h3 style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: 0 }}>
          {translate("newDashboard.feedback.step1.title")}
        </h3>
      </div>
      <p style={{ fontSize: 14, color: "#6b7280", margin: "0 0 20px" }}>
        {translate("newDashboard.feedback.step1.question")}
      </p>
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 6,
        }}
      >
        {[1, 2, 3, 4, 5].map((star) => {
          const active = star <= (hoveredStar || rating);
          return (
            <button
              key={star}
              onClick={() => handleRate(star)}
              onMouseEnter={() => setHoveredStar(star)}
              onMouseLeave={() => setHoveredStar(0)}
              style={{
                border: "none",
                background: "none",
                cursor: "pointer",
                padding: 4,
                transition: "transform 0.1s",
                transform: hoveredStar === star ? "scale(1.2)" : "scale(1)",
              }}
            >
              <svg
                width={starSize}
                height={starSize}
                viewBox="0 0 24 24"
                fill={active ? "#f59e0b" : "none"}
                stroke={active ? "#f59e0b" : "#d1d5db"}
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
              </svg>
            </button>
          );
        })}
      </div>
    </div>
  );

  // ── HAPPY PATH (4-5 stars) ────────────────────────────────────────────
  const HappyStep = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: "0 0 8px" }}>
        {translate("newDashboard.feedback.happy.title").replace("{name}", userName)}
      </h3>
      <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, margin: "0 0 24px" }}>
        {translate("newDashboard.feedback.happy.desc")}
      </p>
      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        <button
          onClick={() => {
            // TODO: POST /api/analytics/trustpilot-redirect-clicked
            window.open(trustpilotUrl, "_blank");
            onClose();
          }}
          style={{
            width: "100%",
            padding: "12px 0",
            backgroundColor: "#00b67a",
            color: "#fff",
            border: "none",
            borderRadius: 10,
            fontSize: 15,
            fontWeight: 700,
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            transition: "background-color 0.15s",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = "#009e69"; }}
          onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = "#00b67a"; }}
        >
          <svg width={18} height={18} viewBox="0 0 24 24" fill="#fff" stroke="none">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
          {translate("newDashboard.feedback.happy.cta")}
        </button>
        <button
          onClick={onClose}
          style={{
            border: "none",
            background: "none",
            cursor: "pointer",
            color: "#9ca3af",
            fontSize: 14,
            fontWeight: 500,
            padding: "8px 0",
            textAlign: "center",
            width: "100%",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "#6b7280"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; }}
        >
          {translate("newDashboard.feedback.happy.later")}
        </button>
      </div>
    </div>
  );

  // ── UNHAPPY PATH (1-3 stars) ──────────────────────────────────────────
  const UnhappyStep = () => (
    <div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: "#111827", margin: "0 0 8px" }}>
        {translate("newDashboard.feedback.unhappy.title")}
      </h3>
      <p style={{ fontSize: 14, color: "#6b7280", lineHeight: 1.6, margin: "0 0 16px" }}>
        {translate("newDashboard.feedback.unhappy.desc")}
      </p>
      <textarea
        value={feedbackText}
        onChange={(e) => setFeedbackText(e.target.value)}
        placeholder={translate("newDashboard.feedback.unhappy.placeholder")}
        rows={4}
        style={{
          width: "100%",
          padding: "12px 14px",
          fontSize: 14,
          border: "1px solid #d1d5db",
          borderRadius: 10,
          outline: "none",
          resize: "vertical",
          color: "#111827",
          marginBottom: 16,
          fontFamily: "inherit",
        }}
      />
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        <ButtonDS
          label={translate("newDashboard.feedback.unhappy.cta")}
          buttonType="primary"
          size="40"
          onClick={handleSubmitFeedback}
          disabled={!feedbackText.trim()}
          className="w-full"
        />
        <button
          onClick={onClose}
          style={{
            border: "none",
            background: "none",
            cursor: "pointer",
            color: "#9ca3af",
            fontSize: 14,
            fontWeight: 500,
            padding: "6px 0",
            textAlign: "center",
            width: "100%",
          }}
          onMouseEnter={(e) => { e.currentTarget.style.color = "#6b7280"; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = "#9ca3af"; }}
        >
          {translate("newDashboard.feedback.unhappy.cancel")}
        </button>
      </div>
    </div>
  );

  // ── THANKS ────────────────────────────────────────────────────────────
  const ThanksStep = () => (
    <div style={{ textAlign: "center", padding: "8px 0" }}>
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: "50%",
          backgroundColor: "#ecfdf5",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          margin: "0 auto 12px",
        }}
      >
        <GlobalIcon iconName="CircleCheckFilledIcon" size="XS" color="#059669" />
      </div>
      <p style={{ fontSize: 16, fontWeight: 700, color: "#111827", margin: 0 }}>
        {translate("newDashboard.feedback.thanks")}
      </p>
    </div>
  );

  return (
    <div style={cardStyle}>
      {/* Close X */}
      <button
        onClick={onClose}
        style={{
          position: "absolute",
          top: 12,
          right: 12,
          border: "none",
          background: "none",
          cursor: "pointer",
          padding: 4,
          borderRadius: 6,
          display: "flex",
        }}
      >
        <GlobalIcon iconName="XIcon" size="XS" color="#d1d5db" />
      </button>

      {step === "rating" && <RatingStep />}
      {step === "happy" && <HappyStep />}
      {step === "unhappy" && <UnhappyStep />}
      {step === "thanks" && <ThanksStep />}
    </div>
  );
};

export default SmartFeedback;
